// src/components/VisualizationGallery.jsx
import React from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import { normalizeMediaUrl } from '../api/base';

const GalleryContainer = styled.div`
  border: 1px solid var(--bdr);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: var(--shadow);
  background: var(--card);
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 8px;
  padding: 16px;
  border-bottom: 1px solid var(--bdr);
  background: var(--card);
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    padding: 12px;
    gap: 6px;
  }
`;

const CategoryTab = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid var(--bdr);
  background: ${props => props.$active ? 'var(--primary)' : 'var(--card)'};
  color: ${props => props.$active ? 'var(--primaryFg)' : 'var(--fg)'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.$active ? 'var(--primary)' : 'var(--primary)'};
    color: var(--primaryFg);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 13px;
  }
`;

const GalleryWrap = styled.div`
  position: relative;
  padding: 16px;
  
  .swiper {
    margin: 0 -8px;
    padding: 0 8px;
  }
  
  .swiper-slide {
    width: auto !important;
    height: auto;
    margin-right: 12px;
    
    &:last-child {
      margin-right: 0;
    }
  }
  
  .swiper-button-next,
  .swiper-button-prev {
    color: var(--primary);
    background: rgba(255, 255, 255, 0.95);
    width: 44px;
    height: 44px;
    border-radius: 50%;
    margin-top: -22px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(0, 0, 0, 0.1);
    
    &:after {
      font-size: 18px;
      font-weight: 700;
    }
    
    &:hover {
      background: rgba(255, 255, 255, 1);
      transform: scale(1.05);
    }
    
    @media (max-width: 768px) {
      width: 36px;
      height: 36px;
      margin-top: -18px;
      
      &:after {
        font-size: 14px;
      }
    }
  }
  
  .swiper-button-prev {
    left: 8px;
  }
  
  .swiper-button-next {
    right: 8px;
  }
`;

const ImageItem = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  height: 404px;
  
  @media (max-width: 768px) {
    height: 300px;
  }
  
  @media (max-width: 480px) {
    height: 250px;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
  
  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

// Разные ширины для изображений (высота фиксированная через CSS)
const getImageWidth = (index, isPlans = false) => {
  if (isPlans) {
    // Для планировок - более узкие (вертикальные пропорции)
    const widths = [280, 200, 240, 180, 220];
    return widths[index % widths.length];
  } else {
    // Для обычных изображений - разные ширины
    const widths = [320, 200, 280, 240, 300, 180, 260, 200];
    return widths[index % widths.length];
  }
};

// Lightbox
const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.95);
  display: grid;
  place-items: center;
  backdrop-filter: blur(4px);
`;

const LBInner = styled.div`
  width: min(1400px, 96vw);
  height: min(92vh, 900px);
  position: relative;
`;

const LBImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const LBClose = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
`;

const LBArrow = styled.button`
  position: absolute;
  top: 50%;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 28px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transform: translateY(-50%);
  backdrop-filter: blur(8px);
  transition: all 0.3s ease;
  
  ${props => props.$left ? 'left: 30px;' : 'right: 30px;'}
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: translateY(-50%) scale(1.1);
  }
  
  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 20px;
    ${props => props.$left ? 'left: 20px;' : 'right: 20px;'}
  }
`;

const LBCounter = styled.div`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

export default function VisualizationGallery({ groups = [] }) {
  const [activeCategory, setActiveCategory] = React.useState(0);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxIndex, setLightboxIndex] = React.useState(0);

  // Фильтруем группы с изображениями
  const validGroups = React.useMemo(() => {
    return groups.filter(group => Array.isArray(group.images) && group.images.length > 0);
  }, [groups]);

  // Текущая активная группа
  const currentGroup = validGroups[activeCategory] || null;
  const currentImages = currentGroup?.images || [];

  // Определяем, является ли текущая группа планировками
  const isPlansGroup = React.useMemo(() => {
    if (!currentGroup) return false;
    const key = currentGroup.key?.toLowerCase() || '';
    const name = currentGroup.name?.toLowerCase() || '';
    return key.includes('plan') || key.includes('layout') || key.includes('floorplan') ||
           name.includes('план') || name.includes('plans') || name.includes('layout');
  }, [currentGroup]);

  // Все изображения для лайтбокса
  const allImages = React.useMemo(() => {
    return validGroups.flatMap(group => group.images);
  }, [validGroups]);

  // Блокировка скролла при лайтбоксе
  React.useEffect(() => {
    if (!lightboxOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setLightboxOpen(false);
      } else if (e.key === 'ArrowLeft') {
        setLightboxIndex(prev => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight') {
        setLightboxIndex(prev => Math.min(allImages.length - 1, prev + 1));
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxOpen, allImages.length]);

  const openLightbox = (imageIndex) => {
    // Находим глобальный индекс изображения
    let globalIndex = 0;
    for (let i = 0; i < activeCategory; i++) {
      globalIndex += validGroups[i].images.length;
    }
    globalIndex += imageIndex;
    
    setLightboxIndex(globalIndex);
    setLightboxOpen(true);
  };

  if (!validGroups.length) {
    return null;
  }

  return (
    <GalleryContainer>
      {/* Табы категорий */}
      {validGroups.length > 1 && (
        <CategoryTabs>
          {validGroups.map((group, index) => (
            <CategoryTab
              key={group.key || index}
              $active={index === activeCategory}
              onClick={() => setActiveCategory(index)}
            >
              {group.name}
            </CategoryTab>
          ))}
        </CategoryTabs>
      )}

      {/* Галерея изображений */}
      <GalleryWrap>
        <Swiper
          modules={[Navigation, FreeMode]}
          navigation
          freeMode={true}
          spaceBetween={0}
          slidesPerView="auto"
          slidesPerGroup={3} // Листаем по 3 изображения
          key={`gallery-${activeCategory}`}
        >
          {currentImages.map((imageUrl, index) => {
            const width = getImageWidth(index, isPlansGroup);
            return (
              <SwiperSlide key={index}>
                <ImageItem
                  style={{
                    width: `${width}px`,
                  }}
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={normalizeMediaUrl(imageUrl)}
                    alt={`${currentGroup.name} ${index + 1}`}
                    loading="lazy"
                  />
                </ImageItem>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </GalleryWrap>

      {/* Лайтбокс */}
      {lightboxOpen && (
        <Lightbox onClick={() => setLightboxOpen(false)}>
          <LBInner onClick={(e) => e.stopPropagation()}>
            <LBClose onClick={() => setLightboxOpen(false)}>×</LBClose>
            
            <LBCounter>
              {lightboxIndex + 1} / {allImages.length}
            </LBCounter>
            
            {lightboxIndex > 0 && (
              <LBArrow
                $left
                onClick={() => setLightboxIndex(prev => Math.max(0, prev - 1))}
              >
                ‹
              </LBArrow>
            )}
            
            {lightboxIndex < allImages.length - 1 && (
              <LBArrow
                onClick={() => setLightboxIndex(prev => Math.min(allImages.length - 1, prev + 1))}
              >
                ›
              </LBArrow>
            )}
            
            <LBImg
              src={normalizeMediaUrl(allImages[lightboxIndex])}
              alt={`Image ${lightboxIndex + 1}`}
            />
          </LBInner>
        </Lightbox>
      )}
    </GalleryContainer>
  );
}